import React, { useEffect, useRef, useState } from 'react';
import { SideDrawerRef } from '../SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import DisplayRichTextString from 'components/RichTextEditorField/components/DisplayRichTextString';
import { Button } from 'ncoded-component-library';
import PreviewQuestionDrawer from '../PreviewQuestionDrawer';
import classNames from 'classnames';
import { Question } from '../../models/QAndA';

import './ShowShortenedRichText.styles.scss';
import './ShowShortenedRichText.styles.responsive.scss';

type ShowShortenedRichTextProps = {
  className?: string;
  question: Question;
  maxHeight?: number;
  hidePrivateInfo?: boolean;
  asAccordion?: boolean;
  updateQuestion: (question: Partial<Question>) => void;
};
//children has to be stringified rich text value
const ShowShortenedRichText: React.FC<ShowShortenedRichTextProps> = (props) => {
  const {
    children,
    className,
    question,
    maxHeight,
    hidePrivateInfo,
    asAccordion = false,
    updateQuestion,
  } = props;
  const [showAll, setShowAll] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { t } = useTranslation();
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [contentContainer, contentContainerRef] = useCallbackRef();
  const resizeObserverRef = useRef<ResizeObserver>();

  const classes = classNames('show-shortened-rich-text', className);

  // calculating if content is overflowing to (or not to) show "show more" button
  useEffect(() => {
    if (!contentContainer) return;
    const onResize: ResizeObserverCallback = ([_]) => {
      const hasOverflowingChildren =
        contentContainer.offsetHeight < contentContainer.scrollHeight ||
        contentContainer.offsetWidth < contentContainer.scrollWidth;

      setIsOverflowing(hasOverflowingChildren);
    };

    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(onResize);
      resizeObserverRef.current.observe(contentContainer as Element);
    }

    return () => {
      resizeObserverRef.current?.unobserve(contentContainer as Element);
    };
  }, [contentContainer]);

  useEffect(() => {
    maxHeight &&
      contentContainer?.style.setProperty('--max-height', `${maxHeight}px`);
  }, [contentContainer, maxHeight]);

  useEffect(() => {
    showAll && contentContainer?.style.setProperty('--max-height', 'unset');
  }, [contentContainer, showAll]);

  return (
    <div className={classes} ref={contentContainerRef}>
      <DisplayRichTextString disableShowMore>
        {children as string}
      </DisplayRichTextString>

      {!showAll && (
        <div
          className={classNames(
            'show-shortened-rich-text__show-more-container',
            {
              visible: isOverflowing,
            },
          )}
        >
          <Button
            variant="link"
            onClick={() =>
              asAccordion ? setShowAll(true) : drawer?.setOpen(true)
            }
          >
            {t('showMore')}
          </Button>
          <PreviewQuestionDrawer
            ref={drawerRef}
            className="show-shortened-rich-text-overflowing-content-preview"
            question={question}
            hidePrivateInfo={hidePrivateInfo}
            onClose={() => drawer.setOpen(false)}
            updateQuestion={updateQuestion}
          />
        </div>
      )}
    </div>
  );
};

export default ShowShortenedRichText;
